import { useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { registerAccount } from "../api/client";
import { useAuth } from "../context/AuthContext";
import FastSendLogo from "../components/FastSendLogo";
import ContactFooter from "../components/ContactFooter";

const ROLE_HOME = { user: "/dashboard", rider: "/rider/dashboard" };
const VEHICLE_TYPES = [
  { value: "bicycle", label: "🚲 Bicycle" },
  { value: "bike", label: "🏍️ Motorcycle / Bike" },
  { value: "van", label: "🚐 Cargo Van" },
  { value: "truck", label: "🚚 Delivery Truck" }
];

// Rider steps mirror the "KYC for Riders/Dispatchers - FastSend.NG" onboarding
// form's own three sections, plus an account-setup step the standalone form
// doesn't need (it's KYC-only; here we also create the login).
const RIDER_STEPS = ["Account", "Personal Information", "Dispatcher Information", "Guarantor/Reference"];

const EXPERIENCE_OPTIONS = [
  { value: "0", label: "0 (No experience)" },
  { value: "1-2", label: "1-2 Years" },
  { value: "3-5", label: "3-5 Years" },
  { value: "5+", label: "Above 5 Years" }
];

const initialForm = {
  name: "", email: "", phone: "", password: "",
  vehicleType: "bike", vehiclePlateNumber: "", referredByCode: "",
  kyc: {
    surname: "", firstName: "", middleName: "",
    gender: "", maritalStatus: "", whatsappNumber: "",
    nin: "", dateOfBirth: "", residentialAddress: "",
    drivingLicenseNumber: "", vehicleOwnership: "", vehicleRegistrationNumber: "",
    ridingExperienceYears: "", drivingExperienceYears: "", hasValidLasdri: "",
    guarantor: { name: "", phone: "", address: "" }
  }
};

// Required fields per rider step, checked before allowing "Next"/"Submit" —
// mirrors exactly which fields the source Google Form marks required.
// "name" and "vehiclePlateNumber" are deliberately absent here for riders —
// they're derived from kyc.surname/firstName/middleName and
// kyc.vehicleRegistrationNumber at submit time (see handleSubmit) instead
// of being asked for twice in two different shapes.
const STEP_REQUIRED_FIELDS = {
  0: ["email", "phone", "password"],
  1: ["kyc.surname", "kyc.firstName", "kyc.gender", "kyc.maritalStatus", "kyc.nin", "kyc.dateOfBirth", "kyc.residentialAddress"],
  2: ["kyc.drivingLicenseNumber", "kyc.vehicleOwnership", "kyc.vehicleRegistrationNumber", "kyc.ridingExperienceYears", "kyc.drivingExperienceYears", "kyc.hasValidLasdri"],
  3: ["kyc.guarantor.name", "kyc.guarantor.phone", "kyc.guarantor.address"]
};

function getAtPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);
}
function setAtPath(obj, path, value) {
  const keys = path.split(".");
  const next = { ...obj };
  let cursor = next;
  keys.forEach((k, i) => {
    if (i === keys.length - 1) {
      cursor[k] = value;
    } else {
      cursor[k] = { ...cursor[k] };
      cursor = cursor[k];
    }
  });
  return next;
}

export default function Register() {
  const { role } = useParams(); // "user" | "rider"
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { account, login } = useAuth();
  const navigate = useNavigate();

  if (account) {
    return <Navigate to={ROLE_HOME[account.role] || "/order"} replace />;
  }

  if (!["user", "rider"].includes(role)) return <Navigate to="/login" replace />;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleKycChange = (path) => (e) => setForm((f) => setAtPath(f, path, e.target.value));
  const handleLasdriChange = (value) => setForm((f) => setAtPath(f, "kyc.hasValidLasdri", value));

  const stepIsValid = (s) => STEP_REQUIRED_FIELDS[s]
    // Bicycles have no plate to register.
    .filter((path) => !(form.vehicleType === "bicycle" && path === "kyc.vehicleRegistrationNumber"))
    .every((path) => {
      const v = getAtPath(form, path);
      return v !== undefined && v !== null && v !== "";
    });

  const handleNext = (e) => {
    e.preventDefault();
    if (!stepIsValid(step)) {
      setError("Please fill in all required fields before continuing.");
      return;
    }
    setError("");
    setStep((s) => Math.min(s + 1, RIDER_STEPS.length - 1));
  };
  const handleBack = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (role === "rider" && !stepIsValid(step)) {
      setError("Please fill in all required fields before submitting.");
      return;
    }
    setLoading(true);
    try {
      const payload =
        role === "rider"
          ? {
              ...form,
              // Derived, not separately collected — see the notes on Step 0
              // and Step 2 above for why.
              name: [form.kyc.surname, form.kyc.firstName, form.kyc.middleName].filter(Boolean).join(" "),
              vehiclePlateNumber: form.kyc.vehicleRegistrationNumber,
              kyc: { ...form.kyc, hasValidLasdri: form.kyc.hasValidLasdri === "yes" }
            }
          : { name: form.name, email: form.email, phone: form.phone, password: form.password };
      const { data } = await registerAccount(role, payload);
      login(data.token, role, data[role]);
      navigate(ROLE_HOME[role]);
    } catch (err) {
      setError(err.response?.data?.error || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="brand-header" style={{ marginBottom: "20px" }}>
        <FastSendLogo size="small" showTagline={true} />
        <h2 style={{ fontSize: "20px", fontWeight: "800", marginTop: "12px", color: "var(--text-primary)" }}>
          {role === "rider" ? "Become a FASTSEND Dispatcher" : "Create Customer Account"}
        </h2>
        <p className="brand-tagline">
          {role === "rider" ? "Start earning on your schedule" : "Send packages across the city instantly"}
        </p>
      </div>

      {role === "rider" && (
        <>
          <div className="step-indicator">
            {RIDER_STEPS.map((_, i) => (
              <span key={i} className={`step-dot ${i === step ? "active" : i < step ? "completed" : ""}`} />
            ))}
          </div>
          <h3 style={{ textAlign: "center", marginBottom: "4px" }}>{RIDER_STEPS[step]}</h3>
          {step > 0 && (
            <p className="hint" style={{ textAlign: "center", marginBottom: "16px" }}>
              This is to complete your onboarding as a FastSend dispatcher — please provide accurate information.
            </p>
          )}
        </>
      )}

      <form onSubmit={step === RIDER_STEPS.length - 1 || role === "user" ? handleSubmit : handleNext}>
        {/* ── STEP 0 (rider) / only step (user): Account ── */}
        {(role === "user" || step === 0) && (
          <>
            {/* Riders provide their name as Surname/First/Middle on the next
                step (as stated on their NIN) — asking for a plain "Full Name"
                here too would mean typing the same detail twice. */}
            {role === "user" && (
              <div className="form-field">
                <label>Full Name</label>
                <input name="name" placeholder="John Doe" value={form.name} onChange={handleChange} required />
              </div>
            )}
            <div className="form-field">
              <label>Email Address</label>
              <input name="email" type="email" placeholder="name@example.com" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-field">
              <label>Phone Number</label>
              <input name="phone" placeholder="+234 800 000 0000" value={form.phone} onChange={handleChange} required />
            </div>
            <div className="form-field">
              <label>Password</label>
              <input name="password" type="password" placeholder="••••••••" value={form.password} onChange={handleChange} required />
            </div>

            {role === "rider" && (
              <>
                <div className="form-field">
                  <label>Vehicle Type</label>
                  <select name="vehicleType" value={form.vehicleType} onChange={handleChange}>
                    {VEHICLE_TYPES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
                {/* Plate number isn't asked here — it's the same detail as
                    "Registration Number of Motorbike/Vehicle" on the
                    Rider/Dispatch Information step, so it's derived from
                    there instead of being asked for twice. */}
                <div className="form-field">
                  <label>Referral Code (Optional)</label>
                  <input name="referredByCode" placeholder="Were you referred by someone? Enter their code" value={form.referredByCode} onChange={handleChange} />
                  <p className="hint">Whoever referred you earns a ₦1,000 voucher once your account is approved.</p>
                </div>
              </>
            )}
          </>
        )}

        {/* ── STEP 1: Personal Information ── */}
        {role === "rider" && step === 1 && (
          <>
            <div className="form-field">
              <label>Surname (as stated on your NIN)</label>
              <input value={form.kyc.surname} onChange={handleKycChange("kyc.surname")} required />
            </div>
            <div className="form-field">
              <label>First Name (as stated on your NIN)</label>
              <input value={form.kyc.firstName} onChange={handleKycChange("kyc.firstName")} required />
            </div>
            <div className="form-field">
              <label>Middle Name (as stated on your NIN, optional)</label>
              <input value={form.kyc.middleName} onChange={handleKycChange("kyc.middleName")} />
            </div>
            <div className="form-field">
              <label>Gender</label>
              <select value={form.kyc.gender} onChange={handleKycChange("kyc.gender")} required>
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-field">
              <label>Marital Status</label>
              <select value={form.kyc.maritalStatus} onChange={handleKycChange("kyc.maritalStatus")} required>
                <option value="">Select...</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widow/Widower</option>
              </select>
            </div>
            <div className="form-field">
              <label>WhatsApp Number (Optional)</label>
              <input value={form.kyc.whatsappNumber} onChange={handleKycChange("kyc.whatsappNumber")} />
            </div>
            <div className="form-field">
              <label>National Identification Number (NIN)</label>
              <input value={form.kyc.nin} onChange={handleKycChange("kyc.nin")} required />
            </div>
            <div className="form-field">
              <label>Date of Birth</label>
              <input type="date" value={form.kyc.dateOfBirth} onChange={handleKycChange("kyc.dateOfBirth")} required />
            </div>
            <div className="form-field">
              <label>Residential Address (include house number)</label>
              <textarea rows={2} style={{ resize: "vertical" }} value={form.kyc.residentialAddress} onChange={handleKycChange("kyc.residentialAddress")} required />
            </div>
          </>
        )}

        {/* ── STEP 2: Rider/Dispatch Information ── */}
        {role === "rider" && step === 2 && (
          <>
            <div className="form-field">
              <label>Valid Driving/Rider License Number</label>
              <input value={form.kyc.drivingLicenseNumber} onChange={handleKycChange("kyc.drivingLicenseNumber")} required />
            </div>
            <div className="form-field">
              <label>Bike/Vehicle Ownership</label>
              <select value={form.kyc.vehicleOwnership} onChange={handleKycChange("kyc.vehicleOwnership")} required>
                <option value="">Select...</option>
                <option value="owned">I own the bike/vehicle</option>
                <option value="leased">The bike/vehicle is leased/rented</option>
                <option value="no_access">I do not have access to a bike/vehicle</option>
              </select>
            </div>
            {form.vehicleType !== "bicycle" && (
              <div className="form-field">
                <label>Registration Number of Motorbike/Vehicle</label>
                <input value={form.kyc.vehicleRegistrationNumber} onChange={handleKycChange("kyc.vehicleRegistrationNumber")} required />
              </div>
            )}
            <div className="form-field">
              <label>Years of Experience Riding a Motorbike</label>
              <select value={form.kyc.ridingExperienceYears} onChange={handleKycChange("kyc.ridingExperienceYears")} required>
                <option value="">Select...</option>
                {EXPERIENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Years of Experience Driving a Car/Van</label>
              <select value={form.kyc.drivingExperienceYears} onChange={handleKycChange("kyc.drivingExperienceYears")} required>
                <option value="">Select...</option>
                {EXPERIENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Do you have a valid LASDRI?</label>
              <div className="row">
                <button type="button" className={form.kyc.hasValidLasdri === "yes" ? "success" : "secondary"} onClick={() => handleLasdriChange("yes")}>Yes</button>
                <button type="button" className={form.kyc.hasValidLasdri === "no" ? "danger" : "secondary"} onClick={() => handleLasdriChange("no")}>No</button>
              </div>
            </div>
          </>
        )}

        {/* ── STEP 3: Guarantor/Reference ── */}
        {role === "rider" && step === 3 && (
          <>
            <div className="form-field">
              <label>Guarantor's Full Name</label>
              <input value={form.kyc.guarantor.name} onChange={handleKycChange("kyc.guarantor.name")} required />
            </div>
            <div className="form-field">
              <label>Guarantor's Contact Telephone Number</label>
              <input value={form.kyc.guarantor.phone} onChange={handleKycChange("kyc.guarantor.phone")} required />
            </div>
            <div className="form-field">
              <label>Guarantor's Contact Address</label>
              <textarea rows={2} style={{ resize: "vertical" }} value={form.kyc.guarantor.address} onChange={handleKycChange("kyc.guarantor.address")} required />
            </div>
          </>
        )}

        {error && <p className="error">{error}</p>}

        {role === "rider" ? (
          <div className="row">
            {step > 0 && <button type="button" className="secondary" onClick={handleBack} disabled={loading}>Back</button>}
            <button type="submit" disabled={loading}>
              {loading ? "Submitting..." : step === RIDER_STEPS.length - 1 ? "Submit Application" : "Next"}
            </button>
          </div>
        ) : (
          <button type="submit" disabled={loading}>
            {loading ? "Creating account..." : "Create Account"}
          </button>
        )}
      </form>

      {role === "rider" && (
        <p className="hint">
          Verification documents can be uploaded after registering. Your account needs
          admin approval before you can go online.
        </p>
      )}

      <p className="text-link">
        Already have an account? <Link to="/login">Log in</Link>
      </p>

      <ContactFooter />
    </div>
  );
}
